import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AddOutlined,
  CampaignOutlined,
  DeleteOutline,
  ListAltOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/Api';
import {
  formatDate,
  getApiErrorMessage,
  getClassDisplayName,
  getStoredTeacher,
  teacherPortalGridItemSx,
  teacherPortalPanelCardSx,
  teacherPortalRowSx,
} from './teacherPortalUtils';

export default function TeacherAnnouncementsScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const classes = Array.isArray(teacher.assignedClasses) ? teacher.assignedClasses : [];
  const [selectedClassId, setSelectedClassId] = useState(classes[0]?._id || '');
  const [announcements, setAnnouncements] = useState([]);
  const [form, setForm] = useState({ title: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/announcements/my');
      setAnnouncements(res.data?.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load announcements.'));
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!selectedClassId || !form.message.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/announcements', {
        title: form.title.trim() || 'Class Announcement',
        message: form.message.trim(),
        classId: selectedClassId,
      });
      toast.success('Announcement sent.');
      setForm({ title: '', message: '' });
      loadAnnouncements();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to send announcement.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted.');
      setAnnouncements((prev) => prev.filter((item) => (item._id || item.id) !== id));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete announcement.'));
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Announcements</Typography>
          <Typography color="text.secondary">Send class announcements to students and parents.</Typography>
        </Box>
        <Button startIcon={<RefreshOutlined />} onClick={loadAnnouncements} disabled={loading} variant="outlined" sx={{ textTransform: 'none' }}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <CampaignOutlined fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    New Announcement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Send a message to a selected class.
                  </Typography>
                </Box>
              </Stack>
              <Stack component="form" onSubmit={handleCreate} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Class</InputLabel>
                  <Select label="Class" value={selectedClassId} onChange={(event) => setSelectedClassId(event.target.value)}>
                    {classes.map((cls) => (
                      <MenuItem key={cls._id} value={cls._id}>{getClassDisplayName(cls)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Title" value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} fullWidth />
                <TextField label="Message" value={form.message} onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))} fullWidth multiline minRows={5} />
                <Button type="submit" variant="contained" startIcon={<AddOutlined />} disabled={saving} sx={{ textTransform: 'none', borderRadius: 1 }}>
                  Send Announcement
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <ListAltOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Announcement List ({announcements.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review announcements sent to your classes.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={1.5}>
                  {announcements.map((item) => {
                    const id = item._id || item.id;
                    return (
                      <Box
                        key={id}
                        sx={teacherPortalRowSx(theme)}
                      >
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography variant="h6" fontWeight={900}>{item.title || 'Announcement'}</Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.5 }}>{item.message}</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>{formatDate(item.createdAt)}</Typography>
                          </Box>
                          <Button color="error" startIcon={<DeleteOutline />} onClick={() => handleDelete(id)} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                            Delete
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })}
                  {!loading && announcements.length === 0 && (
                    <Typography color="text.secondary">No announcements found.</Typography>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
